import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { calendarService } from '../../services/calendarService';
import { supabase } from '../../lib/supabase';
import { format, startOfDay, addWeeks } from 'date-fns';
import SlotManagementGrid from './components/SlotManagementGrid';
import SlotMetricsOverview from './components/SlotMetricsOverview';
import BulkSlotCreator from './components/BulkSlotCreator';
import AutomationMonitor from './components/AutomationMonitor';

const MondaySlotManagementConsole = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [automationEnabled, setAutomationEnabled] = useState(true);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [showBulkCreator, setShowBulkCreator] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [filterWeek, setFilterWeek] = useState('all');
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleBookingId, setRescheduleBookingId] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }
    
    if (userProfile?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }

    loadSlotData();
    setupRealtimeSubscription();
  }, [user, userProfile, navigate]);

  const loadSlotData = async () => {
    try {
      setLoading(true);
      const startDate = startOfDay(new Date())?.toISOString();
      const endDate = addWeeks(new Date(), 8)?.toISOString();
      
      const [slotsData, bookingsData] = await Promise.all([
        calendarService?.getAvailableSlots(startDate, endDate),
        calendarService?.getAllBookings()
      ]);
      
      setSlots(slotsData || []);
      setBookings(bookingsData || []);
      setLastSyncTime(new Date());
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load slot data');
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    const slotsSubscription = supabase
      ?.channel('availability_slots_changes')
      ?.on('postgres_changes', 
        { event: '*', schema: 'public', table: 'availability_slots' },
        (payload) => {
          console.log('Slot change detected:', payload);
          loadSlotData();
        }
      )
      ?.subscribe();

    const bookingsSubscription = supabase
      ?.channel('consultation_bookings_changes')
      ?.on('postgres_changes',
        { event: '*', schema: 'public', table: 'consultation_bookings' },
        (payload) => {
          console.log('Booking change detected:', payload);
          loadSlotData();
        }
      )
      ?.subscribe();

    return () => {
      slotsSubscription?.unsubscribe();
      bookingsSubscription?.unsubscribe();
    };
  };

  const handleGenerateMondaySlots = async () => {
    try {
      setLoading(true);
      await calendarService?.generateMondaySlots();
      await loadSlotData();
    } catch (err) {
      setError(err?.message || 'Failed to generate Monday slots');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSlot = async (slotId) => {
    if (!window.confirm('Are you sure you want to delete this slot?')) {
      return;
    }
    
    try {
      await calendarService?.deleteAvailabilitySlot(slotId);
      await loadSlotData();
    } catch (err) {
      setError(err?.message || 'Failed to delete slot');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedSlots?.length === 0) {
      alert('Please select slots to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedSlots?.length} slot(s)?`)) {
      return;
    }

    try {
      setLoading(true);
      await Promise.all(
        selectedSlots?.map(slotId => calendarService?.deleteAvailabilitySlot(slotId))
      );
      setSelectedSlots([]);
      await loadSlotData();
    } catch (err) {
      setError(err?.message || 'Failed to delete slots');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSlotAvailability = async (slotId, currentStatus) => {
    try {
      await calendarService?.updateAvailabilitySlot(slotId, {
        isAvailable: !currentStatus
      });
      await loadSlotData();
    } catch (err) {
      setError(err?.message || 'Failed to update slot');
    }
  };

  const handleAcceptBooking = async (bookingId) => {
    if (!window.confirm('Accept this consultation booking?')) {
      return;
    }

    try {
      setLoading(true);
      
      // Get booking details before updating
      const booking = bookings?.find(b => b?.id === bookingId);
      if (!booking) {
        throw new Error('Booking not found');
      }
      
      // Update booking status to confirmed
      await calendarService?.updateBooking(bookingId, { status: 'confirmed' });
      
      // Send confirmation email using Supabase client (handles CORS and auth properly)
      try {
        console.log('Sending confirmation email for booking:', booking?.id);
        
        const { data, error: emailError } = await supabase?.functions?.invoke('send-booking-notification', {
          body: {
            id: booking?.id,
            client_email: booking?.clientEmail,
            client_name: booking?.clientName,
            scheduled_start: booking?.scheduledStart,
            scheduled_end: booking?.scheduledEnd,
            service_interest: booking?.serviceInterest,
            company: booking?.company,
            client_phone: booking?.clientPhone,
            title: booking?.title,
            description: booking?.description
          }
        });
        
        if (emailError) {
          console.error('Failed to send confirmation email:', emailError);
        } else {
          console.log('Confirmation email sent successfully:', data);
        }
      } catch (emailError) {
        console.error('Error sending confirmation email:', emailError);
        // Don't throw - email failure shouldn't block the acceptance
      }
      
      await loadSlotData();
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to accept booking');
    } finally {
      setLoading(false);
    }
  };

  const handleDeclineBooking = async (bookingId) => {
    if (!window.confirm('Decline this consultation booking? This will cancel the booking and free up the time slot.')) {
      return;
    }

    try {
      setLoading(true);
      await calendarService?.updateBooking(bookingId, { status: 'cancelled' });
      await loadSlotData();
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to decline booking');
    } finally {
      setLoading(false);
    }
  };

  const handleRescheduleBooking = (bookingId, currentSlotId) => {
    setRescheduleBookingId(bookingId);
    setShowRescheduleModal(true);
  };

  const handleRescheduleSubmit = async (newSlotId) => {
    if (!rescheduleBookingId || !newSlotId) {
      return;
    }

    try {
      setLoading(true);
      
      // Get the new slot details
      const newSlot = slots?.find(s => s?.id === newSlotId);
      if (!newSlot) {
        throw new Error('Selected slot not found');
      }

      // Update booking with new slot and times
      await calendarService?.updateBooking(rescheduleBookingId, {
        slotId: newSlotId,
        scheduledStart: newSlot?.startTime,
        scheduledEnd: newSlot?.endTime,
        status: 'confirmed'
      });

      setShowRescheduleModal(false);
      setRescheduleBookingId(null);
      await loadSlotData();
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to reschedule booking');
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = () => {
    const now = new Date();
    const upcomingSlots = slots?.filter(slot => new Date(slot?.startTime) > now);
    const mondaySlots = upcomingSlots?.filter(slot => {
      const slotDate = new Date(slot?.startTime);
      return slotDate?.getDay() === 1; // Monday
    });
    
    const bookedSlotIds = bookings
      ?.filter(b => b?.status !== 'cancelled')
      ?.map(b => b?.slotId);
    
    const availableSlots = mondaySlots?.filter(slot => 
      slot?.isAvailable && !bookedSlotIds?.includes(slot?.id)
    );
    
    const bookedSlots = mondaySlots?.filter(slot => 
      bookedSlotIds?.includes(slot?.id)
    );

    const blockedSlots = mondaySlots?.filter(slot => !slot?.isAvailable);

    // Calculate week coverage
    const weeksWithSlots = new Set();
    mondaySlots?.forEach(slot => {
      const weekStart = startOfDay(new Date(slot?.startTime));
      weeksWithSlots?.add(weekStart?.toISOString());
    });

    return {
      totalActive: mondaySlots?.length || 0,
      available: availableSlots?.length || 0,
      booked: bookedSlots?.length || 0,
      blocked: blockedSlots?.length || 0,
      weeksCovered: weeksWithSlots?.size || 0,
      utilizationRate: mondaySlots?.length > 0 
        ? Math.round((bookedSlots?.length / mondaySlots?.length) * 100) 
        : 0
    };
  };

  const metrics = calculateMetrics();

  if (loading && slots?.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading slot management console...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Monday Slot Management Console | Northstar Analytics</title>
        <meta name="description" content="Administrative dashboard for Monday consultation slot generation and management" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Header />
        
        <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h1 className="text-3xl font-bold text-foreground mb-2">
                    Monday Slot Management Console
                  </h1>
                  <p className="text-muted-foreground">
                    Administrative control center for Monday consultation slot generation and management
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={loadSlotData}
                    iconName="RefreshCw"
                    iconPosition="left"
                    disabled={loading}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="default"
                    onClick={() => setShowBulkCreator(true)}
                    iconName="Plus"
                    iconPosition="left"
                  >
                    Bulk Create Slots
                  </Button>
                </div>
              </div>
              {lastSyncTime && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Icon name="Clock" size={12} />
                  Last synced: {format(lastSyncTime, 'MMM dd, yyyy • h:mm:ss a')}
                </p>
              )}
            </div>

            {/* Error Display */}
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-red-900 mb-1">Error</h3>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
                <button
                  onClick={() => setError('')}
                  className="ml-auto text-red-600 hover:text-red-800"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            )}

            {/* Metrics Overview */}
            <SlotMetricsOverview metrics={metrics} />

            {/* Automation Monitor */}
            <AutomationMonitor
              automationEnabled={automationEnabled}
              onToggleAutomation={() => setAutomationEnabled(!automationEnabled)}
              lastSyncTime={lastSyncTime}
              onGenerateSlots={handleGenerateMondaySlots}
              loading={loading}
            />

            {/* Slot Management Controls */}
            <div className="bg-white rounded-xl shadow-elevation-1 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Settings" size={20} />
                  Slot Management Controls
                </h2>
                {selectedSlots?.length > 0 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleBulkDelete}
                    iconName="Trash2"
                    iconPosition="left"
                  >
                    Delete Selected ({selectedSlots?.length})
                  </Button>
                )}
              </div>
              
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    Filter by Week
                  </label>
                  <select
                    value={filterWeek}
                    onChange={(e) => setFilterWeek(e?.target?.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="all">All Upcoming Weeks</option>
                    <option value="week1">Week 1 (Next Monday)</option>
                    <option value="week2">Week 2</option>
                    <option value="week3">Week 3</option>
                    <option value="week4">Week 4</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Slot Management Grid */}
            <SlotManagementGrid
              slots={slots}
              bookings={bookings}
              selectedSlots={selectedSlots}
              onSelectSlot={(slotId) => {
                setSelectedSlots(prev => 
                  prev?.includes(slotId) 
                    ? prev?.filter(id => id !== slotId)
                    : [...prev, slotId]
                );
              }}
              onSelectAll={() => {
                if (selectedSlots?.length === slots?.length) {
                  setSelectedSlots([]);
                } else {
                  setSelectedSlots(slots?.map(s => s?.id));
                }
              }}
              onDeleteSlot={handleDeleteSlot}
              onToggleAvailability={handleToggleSlotAvailability}
              onAcceptBooking={handleAcceptBooking}
              onDeclineBooking={handleDeclineBooking}
              onRescheduleBooking={handleRescheduleBooking}
              filterWeek={filterWeek}
            />

            {/* Bulk Slot Creator Modal */}
            {showBulkCreator && (
              <BulkSlotCreator
                onClose={() => setShowBulkCreator(false)}
                onSuccess={() => {
                  setShowBulkCreator(false);
                  loadSlotData();
                }}
              />
            )}

            {/* Reschedule Modal */}
            {showRescheduleModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-elevation-3 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                  <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-foreground">Reschedule Consultation</h2>
                    <button
                      onClick={() => {
                        setShowRescheduleModal(false);
                        setRescheduleBookingId(null);
                      }}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <Icon name="X" size={20} />
                    </button>
                  </div>
                  <div className="p-6">
                    <p className="text-muted-foreground mb-4">Select a new available time slot:</p>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {slots
                        ?.filter(slot => {
                          const slotStatus = bookings?.find(b => b?.slotId === slot?.id && b?.status !== 'cancelled');
                          return slot?.isAvailable && !slotStatus && new Date(slot?.startTime) > new Date();
                        })
                        ?.map(slot => (
                          <button
                            key={slot?.id}
                            onClick={() => handleRescheduleSubmit(slot?.id)}
                            className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-foreground">
                                  {format(new Date(slot?.startTime), 'EEEE, MMMM dd, yyyy')}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {format(new Date(slot?.startTime), 'h:mm a')} - {format(new Date(slot?.endTime), 'h:mm a')}
                                </p>
                              </div>
                              <Icon name="ChevronRight" size={20} className="text-muted-foreground" />
                            </div>
                          </button>
                        ))}
                    </div>
                    {slots?.filter(slot => {
                      const slotStatus = bookings?.find(b => b?.slotId === slot?.id && b?.status !== 'cancelled');
                      return slot?.isAvailable && !slotStatus && new Date(slot?.startTime) > new Date();
                    })?.length === 0 && (
                      <div className="text-center py-8">
                        <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">No available slots found</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default MondaySlotManagementConsole;